import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexGrid,
  ApexLegend,
  ApexMarkers,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis
} from 'ng-apexcharts';
import {
  DashboardClientScope,
  DashboardMembersOverTimePoint
} from 'src/app/models/DashboardMetrics';
import { DashboardMetricsService } from 'src/app/services/dashboard-metrics.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { parseDateOnly, toDateKey } from 'src/app/utils/date-only';
import { getErrorMessage } from 'src/app/utils/error-message';

export interface MembersOverTimeChart {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  colors: string[];
  dataLabels: ApexDataLabels;
  stroke: ApexStroke;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis[];
  markers: ApexMarkers;
  legend: ApexLegend;
  grid: ApexGrid;
  tooltip: ApexTooltip;
}

const LEGAL = 'LEGAL';
const IDP = 'IDP';

/** Tight scale around a series so a change of tens of members is visible on a base of thousands. */
function axisRange(values: number[]): { min: number; max: number } {
  const low = Math.min(...values);
  const high = Math.max(...values);
  const span = Math.max(high - low, 1);
  const pad = Math.max(span * 0.4, 15);
  const rawMin = Math.max(0, low - pad);
  const rawMax = high + pad;
  const step = niceStep((rawMax - rawMin) / 4);
  return {
    min: Math.max(0, Math.floor(rawMin / step) * step),
    max: Math.ceil(rawMax / step) * step
  };
}

function niceStep(value: number): number {
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const residual = value / magnitude;
  const nice = residual <= 1 ? 1 : residual <= 2 ? 2 : residual <= 5 ? 5 : 10;
  return nice * magnitude;
}

@UntilDestroy()
@Component({
  selector: 'vex-members-over-time-widget',
  templateUrl: './members-over-time-widget.component.html',
  styleUrls: ['./members-over-time-widget.component.scss']
})
export class MembersOverTimeWidgetComponent implements OnChanges {
  @Input() scope: DashboardClientScope = 'mine';

  loading = false;
  chart: MembersOverTimeChart | null = null;

  constructor(
    private dashboardMetricsService: DashboardMetricsService,
    private snackbarService: SnackbarService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['scope']) {
      this.loadMembers();
    }
  }

  get scopeLabel(): string {
    return this.scope === 'all' ? 'All Clients' : 'My Clients';
  }

  get emptyMessage(): string {
    return this.scope === 'all'
      ? 'No active members in the last 6 months.'
      : 'No active members for your clients in the last 6 months.';
  }

  private loadMembers(): void {
    this.loading = true;
    this.dashboardMetricsService.getMembersOverTime(this.scope)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (result) => {
          this.chart = this.buildChart(result.points ?? []);
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          this.chart = null;
          this.snackbarService.open(
            getErrorMessage(err, 'Failed to load members over time.'),
            'error'
          );
        }
      });
  }

  private buildChart(points: DashboardMembersOverTimePoint[]): MembersOverTimeChart | null {
    const byMonth = new Map<string, { legal: number; idp: number }>();
    for (const point of points) {
      const key = toDateKey(point.monthStart);
      if (!key) {
        continue;
      }

      const entry = byMonth.get(key) ?? { legal: 0, idp: 0 };
      const product = (point.productTypeName ?? '').trim().toUpperCase();
      if (product === LEGAL) {
        entry.legal = point.memberCount ?? 0;
      } else if (product === IDP) {
        entry.idp = point.memberCount ?? 0;
      }
      byMonth.set(key, entry);
    }

    const months = [...byMonth.keys()].sort();
    const legal = months.map(month => byMonth.get(month)?.legal ?? 0);
    const idp = months.map(month => byMonth.get(month)?.idp ?? 0);
    if (months.length === 0 || legal.concat(idp).every(count => count === 0)) {
      return null;
    }

    return {
      series: [
        { name: LEGAL, data: legal },
        { name: IDP, data: idp }
      ],
      chart: {
        type: 'line',
        height: 280,
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: 'inherit'
      },
      colors: ['#5C6BC0', '#43A047'],
      dataLabels: { enabled: false },
      stroke: { curve: 'straight', width: 3 },
      markers: {
        size: 4,
        strokeWidth: 2,
        strokeColors: '#fff',
        hover: { size: 6 }
      },
      xaxis: {
        categories: months.map(month => this.monthLabel(month)),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: '#80868b', fontSize: '12px' } }
      },
      yaxis: [
        this.productAxis(LEGAL, legal, '#5C6BC0', false),
        this.productAxis(IDP, idp, '#43A047', true)
      ],
      legend: {
        position: 'bottom',
        horizontalAlign: 'left',
        fontSize: '12px',
        markers: { width: 10, height: 10, radius: 2 },
        itemMargin: { horizontal: 12 }
      },
      grid: {
        borderColor: 'rgba(0, 0, 0, 0.06)',
        strokeDashArray: 0,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } }
      },
      tooltip: {
        shared: true,
        y: {
          formatter: (value: number) => Math.round(value).toLocaleString('en-US')
        }
      }
    };
  }

  private productAxis(name: string, values: number[], color: string, opposite: boolean): ApexYAxis {
    const range = axisRange(values);
    return {
      seriesName: name,
      opposite,
      min: range.min,
      max: range.max,
      tickAmount: 4,
      forceNiceScale: false,
      labels: {
        style: { colors: color, fontSize: '12px' },
        formatter: (value: number) => Math.round(value).toLocaleString('en-US')
      }
    };
  }

  private monthLabel(value: string): string {
    const date = parseDateOnly(value);
    if (!date) {
      return value;
    }
    return date.toLocaleDateString('en-US', { month: 'short' });
  }
}
